import { Notification } from '@/components/Notification';
import { useNotification } from '@/hooks/useNotification';
import { useOrg } from '@/organization/useOrg';
import { useEnumHelpers } from '@/queries/useEnumHelpers.ts';
import {
  useGetSubproject,
  useUpdateSubprojectDetails,
} from '@/queries/useSubprojects.ts';
import { Box, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SubprojectBuildingDetails } from '../../stores/SubprojectStore.ts';
import { BaseDecarbFooterBar } from '../components/BaseDecarbFooterBar.tsx';
import { usePencilsDown } from '../hooks/usePencilsDown.ts';
import { AirExchangeFactor } from './AirExchangeFactor.tsx';
import { BuildingUsage } from './BuildingUsages.tsx';
import { IncludedSystems } from './IncludedSystems.tsx';

export const BuildingPurpose: React.FC = () => {
  const [subproject, setSubproject] = useState<SubprojectBuildingDetails>();
  const { subId } = useParams();
  const { selectedOrg } = useOrg();
  const navigate = useNavigate();
  const pencilsDown = usePencilsDown();
  const { notification, showError, hideNotification } = useNotification();

  const { data: fetchedSubproject } = useGetSubproject(
    selectedOrg?.id ?? '',
    subId || '',
  );
  const updateSubproject = useUpdateSubprojectDetails(
    selectedOrg?.id ?? '',
    subId || '',
  );
  const pageWidth = '85vw';
  const { technologyCategoriesBuildings } = useEnumHelpers();

  const nextButtonIsDisabled = subproject
    ? subproject.buildingUsages.length === 0 ||
      subproject.enabledTechnologyCategories.length === 0 ||
      !subproject.airExchangeFactor ||
      subproject.airExchangeFactor === 0
    : false;

  useEffect(() => {
    if (fetchedSubproject) {
      setSubproject(fetchedSubproject);
    }
  }, [fetchedSubproject]);

  const saveSubprojectState = () => {
    if (!subproject) return null;

    const updatedSubproject = structuredClone(subproject);
    const selectedTechnologyCategories =
      updatedSubproject?.enabledTechnologyCategories;
    const allTechnologyCategories = technologyCategoriesBuildings.map(
      (item) => item.name,
    );

    if (selectedTechnologyCategories) {
      updatedSubproject.enabledTechnologyCategories = [
        ...selectedTechnologyCategories,
      ].sort(
        (a, b) =>
          allTechnologyCategories.indexOf(a) -
          allTechnologyCategories.indexOf(b),
      );
    }
    return updatedSubproject;
  };

  const handleNextClick = () => {
    if (pencilsDown) {
      navigate('../spaceusage');
      return;
    }

    const updatedSubproject = saveSubprojectState();
    if (!updatedSubproject) {
      return;
    }

    updateSubproject.mutate(updatedSubproject, {
      onSuccess: () => {
        navigate('../spaceusage');
      },
      onError: (error) => {
        showError(`Failed to save: ${error.message}`);
      },
    });
  };

  const handleBackClick = () => {
    if (pencilsDown) {
      navigate('../');
      return;
    }

    const updatedSubproject = saveSubprojectState();
    if (!updatedSubproject) {
      return;
    }

    updateSubproject.mutate(updatedSubproject, {
      onSuccess: () => {
        navigate('../');
      },
      onError: (error) => {
        showError(`Failed to save: ${error.message}`);
      },
    });
  };

  if (!selectedOrg) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          Please select an organization from the user menu to access this
          project.
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          variant={'h3'}
          className={'main-header'}
          sx={{ m: 3, fontSize: 32, textAlign: 'center' }}
        >
          Building&apos;s Purpose
        </Typography>
        <Paper
          sx={{
            width: pageWidth,
            m: 2,
            p: 4,
            pt: 6,
          }}
        >
          <BuildingUsage
            subproject={subproject}
            onUpdate={setSubproject}
            readOnly={pencilsDown}
          />
          <IncludedSystems
            subproject={subproject}
            onUpdate={setSubproject}
            readOnly={pencilsDown}
          />
          <AirExchangeFactor
            subproject={subproject}
            onUpdate={setSubproject}
            readOnly={pencilsDown}
          />
        </Paper>
      </Box>
      <BaseDecarbFooterBar
        onBackClick={handleBackClick}
        onNextClick={handleNextClick}
        nextIsDisabled={nextButtonIsDisabled}
        nextIsDisabledText={'INCOMPLETE DATA'}
        onReturnToHubClick={handleBackClick}
        returnText= {pencilsDown
            ? 'RETURN TO DECARBONIZATION HUB'
            : 'SAVE & RETURN TO DECARBONIZATION HUB'} 
      />
      <Notification notification={notification} onClose={hideNotification} />
    </React.Fragment>
  );
};
